import { toast } from 'react-toastify';
import React from 'react';
import ApiClient from '../services/api-client';
import { CustomConfirmAlert as confirmAlert } from '../components/custom-confirm-alert';
import Commons from '../util/commons';
import MyDataLogo from '../img/mydata.png';

export function useSaleSubmit({
    getValues,
    refSalesEntries,
    saleMoreTaxesGridRef,
    iframePrintTemplate,
    customerCardRef,
    mydata,
    totals,
    template,
    setBlockuiLoading,
    scrollInnerContainer,
    afterPrintProcess,
    showErrorDetailsModal,
    buildPrintSale,
    prepareSale,
    t,
    SELECTED_COMPANY,
}) {
    function buildPrintSaleData() {
        const sale = getValues();
        sale.entries = refSalesEntries.current.getData().entries;
        sale.saleInstanceMoreTaxes = saleMoreTaxesGridRef.current.getData();
        sale.totals = totals;
        const config = {
            nameFromNode: node => node.product.label,
            muFromNode: node => node.measurementUnit.value,
            whTaxFromNode: node => node.taxes?.withHoldTax?.amountValue ?? "0.00",
            vatFromNode: node => node.vat.label,
        };
        const psale = buildPrintSale(sale, config);
        if (template) {
            psale.LOGO = template.logo;
            psale.IBAN = template.iban;
            psale.MOREINFO = template.moreInfo;
        }
        return psale;
    }

    function myDataErrorMessageWithLink(error) {
        return (
            <div>
                {error.message}
                <span style={{ marginLeft: 10 }}>
                    <button className="btn btn-skin btn-sm" onClick={() => showErrorDetailsModal(error)}>{t("More")}</button>
                </span>
            </div>
        );
    }

    function validate() {
        if (refSalesEntries.current.hasError()) {
            toast.error(t("entriesErrors"), 5000);
            return false;
        }
        if (saleMoreTaxesGridRef.current.hasError()) {
            toast.error(t("entriesMoreTaxesErrors"), 5000);
            return false;
        }
        const _entries = refSalesEntries.current.getData();
        if (_entries.entries.length === 0) {
            toast.error(t("entriesErrorsCount"), 5000);
            return false;
        }
        const hasZeroVatWithoutException = _entries.entries.some(
            e => e.vat && e.vat.value === "VAT_0" && !e.vatCategoryException
        );
        if (hasZeroVatWithoutException) {
            toast.error(t("entriesZeroVatNoException"), 5000);
            return false;
        }
        const formValues = getValues();
        const country = formValues.country;
        const taxpayerId = formValues.taxpayerId;
        if (country && country.value === "GR" && !Commons.validateGR(taxpayerId) && (formValues.invoiceType.myDataInvoiceType == "invoice1_1" || formValues.invoiceType.myDataInvoiceType == "invoice2_1")) {
            toast.error(t("invalidGreekTaxpayerId"), 5000);
            return false;
        }
        return true;
    }

    function onSuccessSave(response, sale, _print) {
        const psale = buildPrintSaleData();
        if (response.data.response != undefined) {
            const resp = response.data.response[0];
            if (resp.errors != null) {
                resp.errors.error.forEach(error => {
                    console.error(error);
                    if (error.code == 201) {
                        toast.error(myDataErrorMessageWithLink(error));
                    } else {
                        toast.error(error.message);
                    }
                });
                setBlockuiLoading(false);
            } else {
                if (resp.statusCode === "Success") {
                    const mark = resp.invoiceMark;
                    psale.INVOICEMARK = mark;
                    psale.INVOICEUID = resp.invoiceUid;
                    psale.QRURL = resp.qrUrl;
                    if (mark) {
                        toast.success(<p><img style={{ width: 80 }} src={MyDataLogo} />{t("invoiceSended", { mark })}</p>, { autoClose: 20000 });
                    } else {
                        toast.success(t("saveSucceded"));
                    }
                    scrollInnerContainer({ top: 0 });
                } else if (SELECTED_COMPANY.myDataProvider === "MYDATA") {
                    toast.error(t("Error"));
                }
                if (_print) iframePrintTemplate.current.print(psale);
                sale.invoiceUid = psale.INVOICEUID;
                afterPrintProcess(sale);
            }
        } else {
            if (_print) iframePrintTemplate.current.print(psale);
            sale.invoiceUid = psale.INVOICEUID;
            afterPrintProcess(sale);
            if (SELECTED_COMPANY.myDATAProvider === "MYDATA") {
                toast.success(t("saveSucceded"));
            }
            scrollInnerContainer({ top: 0 });
        }
    }

    function saveSale(_sale, _print = false, _mydata = mydata) {
        if (!validate()) return;

        const sale = prepareSale(_sale);
        console.debug(sale);

        Commons.setDefault("notes", _sale.notes);

        confirmAlert({
            title: t("submitConfirm"),
            message: sale.isPreSale ? t("areYouSurePreSale") : _mydata ? t("areYouSureMyDataOn") : t("areYouSureMyDataOff"),
            buttons: [
                {
                    label: t('Yes'),
                    className: "accept",
                    onClick: () => {
                        setBlockuiLoading(true);
                        ApiClient.saveSaleWithEntries(SELECTED_COMPANY.id, sale).then(
                            response => onSuccessSave(response, sale, _print),
                            error => {
                                setBlockuiLoading(false);
                                toast.error("Error: " + error.response.data.message);
                                console.error(error.response);
                            }
                        );
                    }
                },
                { label: t('No'), onClick: () => { } }
            ],
        });
    }

    const onSubmit = (sale, e) => {
        e.preventDefault();
        if (sale.invoiceType.myDataInvoiceType === "invoice00_1" || sale.invoiceType.myDataInvoiceType === "invoice1_01") {
            sale.isPreSale = true;
            sale.mydata = false;
            saveSale(sale, false, false);
        } else {
            sale.isPreSale = false;
            sale.mydata = mydata ? mydata : false;
            saveSale(sale, false, sale.mydata);
        }
    };

    const onSubmitDraft = (sale, e) => {
        e?.preventDefault();
        if (sale.invoiceType.myDataInvoiceType === "invoice00_1" || sale.invoiceType.myDataInvoiceType === "invoice1_01") {
            sale.isPreSale = true;
        } else {
            sale.isPreSale = false;
        }
        sale.mydata = false;
        saveSale(sale, false, false);
    };

    const onError = (error) => {
        console.error(error);
        const el_name = Object.keys(error)[0];
        if (el_name === "customer") {
            const yOffset = -10;
            const y = customerCardRef.current?.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        } else {
            const el = document.getElementsByName(el_name)?.[0];
            if (el) {
                const yOffset = -10;
                const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                scrollInnerContainer({ top: y, behavior: 'smooth' });
            }
        }
    };

    const handlePrintAndSave = () => {
        const sale = getValues();
        if (sale.invoiceType.myDataInvoiceType === "invoice00_1" || sale.invoiceType.myDataInvoiceType === "invoice1_01") {
            sale.isPreSale = true;
            sale.mydata = false;
        } else {
            sale.isPreSale = false;
            sale.mydata = mydata ? mydata : false;
        }
        saveSale(sale, true);
    };

    const handlePrint = (e) => {
        e.preventDefault();
        iframePrintTemplate.current.print(buildPrintSaleData());
    };

    return { onSubmit, onSubmitDraft, onError, handlePrint, handlePrintAndSave };
}
